import { useAuth } from '@/hooks/useAuth';
import { useAdminSearchUsers } from '@/hooks/useAdminUsers';
import { cn } from '@/lib/utils';
import { LogOut, Moon, Sun, Search, Loader2, User, Mail, ArrowLeft } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminMobilePageTitle, adminMobileParentPath } from '@/constants/admin-nav';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

interface TopBarProps {
    collapsed: boolean;
    isMobile: boolean;
}

function useDebounce<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);
    return debounced;
}

export function TopBar({ collapsed, isMobile }: TopBarProps) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [darkMode, setDarkMode] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const pageTitle = adminMobilePageTitle(pathname);
    const backTo = adminMobileParentPath(pathname);

    const debouncedQuery = useDebounce(searchQuery.trim(), SEARCH_DEBOUNCE_MS);
    const { data: searchResults = [], isFetching } = useAdminSearchUsers(
        debouncedQuery,
        { searchByEmail: true, limit: 15 }
    );
    const showDropdown = searchOpen && searchQuery.trim().length >= MIN_SEARCH_LENGTH;
    const showMobileSearch = isMobile && mobileSearchOpen;

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
                setSearchOpen(false);
                if (isMobile) setMobileSearchOpen(false);
            }
        }
        function handleEscape(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                setSearchOpen(false);
                setSearchQuery('');
                setMobileSearchOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isMobile]);

    function toggleTheme() {
        const html = document.documentElement;
        if (darkMode) {
            html.classList.remove('dark');
        } else {
            html.classList.add('dark');
        }
        setDarkMode(!darkMode);
    }

    function handleSelectUser(userId: string) {
        setSearchQuery('');
        setSearchOpen(false);
        setMobileSearchOpen(false);
        navigate(`/users/${userId}`);
    }

    const searchInput = (
        <div className="relative w-full min-w-0">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
                type="text"
                placeholder={isMobile ? 'Buscar usuarios...' : 'Buscar usuarios por nombre o email...'}
                value={searchQuery}
                autoFocus={showMobileSearch}
                onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                }}
                onFocus={() => searchQuery.trim().length >= MIN_SEARCH_LENGTH && setSearchOpen(true)}
                className={cn(
                    'w-full h-9 pl-9 pr-4 rounded-lg text-sm',
                    'bg-muted/50 border border-transparent',
                    'text-foreground placeholder:text-muted-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50',
                    'transition-all duration-200'
                )}
            />
            {isFetching && (
                <Loader2 size={14} className="absolute right-10 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
            )}
            {!isFetching && searchQuery.trim().length > 0 && searchQuery.trim().length < MIN_SEARCH_LENGTH && (
                <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono text-muted-foreground bg-background border border-border">
                    {MIN_SEARCH_LENGTH}+ caracteres
                </kbd>
            )}
            {!isFetching && searchQuery.trim().length >= MIN_SEARCH_LENGTH && (
                <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono text-muted-foreground bg-background border border-border">
                    ⌘K
                </kbd>
            )}

            {showDropdown && (
                <div
                    className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-card shadow-lg z-50 max-h-[min(320px,60vh)] overflow-y-auto"
                    role="listbox"
                >
                    {isFetching && searchResults.length === 0 ? (
                        <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
                            <Loader2 size={16} className="animate-spin shrink-0" />
                            Buscando usuarios...
                        </div>
                    ) : searchResults.length === 0 ? (
                        <div className="px-4 py-4 text-sm text-muted-foreground">
                            No se encontraron usuarios para &quot;{searchQuery.trim()}&quot;
                        </div>
                    ) : (
                        <ul className="py-1">
                            {searchResults.map((u) => (
                                <li key={u.user_id}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelectUser(u.user_id)}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/60 focus:bg-muted/60 focus:outline-none transition-colors"
                                        role="option"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0">
                                            {u.avatar_url ? (
                                                <img
                                                    src={u.avatar_url}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                    referrerPolicy="no-referrer"
                                                />
                                            ) : (
                                                <User size={16} className="text-muted-foreground" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-foreground truncate">
                                                {u.display_name || 'Sin nombre'}
                                            </p>
                                            {u.email && (
                                                <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                                    <Mail size={10} /> {u.email}
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-[10px] text-muted-foreground shrink-0 hidden sm:inline">
                                            {u.captures_count ?? 0} capturas
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <header
            className={cn(
                'fixed top-0 right-0 z-20',
                'bg-background/80 backdrop-blur-lg border-b border-border',
                'flex items-center gap-2 sm:gap-3 px-3 sm:px-6',
                'transition-sidebar',
                isMobile
                    ? 'left-0'
                    : collapsed
                        ? 'left-[var(--sidebar-collapsed-width)]'
                        : 'left-[var(--sidebar-width)]'
            )}
            style={{
                height: 'calc(var(--topbar-height) + var(--safe-area-top))',
                paddingTop: 'var(--safe-area-top)',
                paddingLeft: isMobile ? 'max(0.75rem, var(--safe-area-left))' : undefined,
                paddingRight: isMobile ? 'max(0.75rem, var(--safe-area-right))' : undefined,
            }}
        >
            {isMobile && (
                showMobileSearch ? (
                    <div className="flex-1 min-w-0" ref={searchContainerRef}>
                        {searchInput}
                    </div>
                ) : (
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        {backTo ? (
                            <button
                                type="button"
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                                aria-label="Volver"
                                onClick={() => navigate(backTo)}
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                        ) : (
                            <img
                                src="/aipeces-mark-dark-ui.png"
                                alt=""
                                className="h-7 w-7 rounded-lg shrink-0 object-contain"
                            />
                        )}
                        <h1 className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight">
                            {pageTitle}
                        </h1>
                    </div>
                )
            )}

            {!isMobile && (
                <div className="flex items-center gap-2 flex-1 min-w-0 max-w-md" ref={searchContainerRef}>
                    {searchInput}
                </div>
            )}

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                {isMobile && !showMobileSearch && (
                    <button
                        type="button"
                        onClick={() => setMobileSearchOpen(true)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="Buscar usuarios"
                    >
                        <Search size={18} />
                    </button>
                )}

                <button
                    type="button"
                    onClick={toggleTheme}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title={darkMode ? 'Modo claro' : 'Modo oscuro'}
                >
                    {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>

                <div className="h-6 w-px bg-border mx-0.5 sm:mx-1 hidden sm:block" />

                {user && (
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="text-right hidden md:block">
                            <p className="text-sm font-medium text-foreground leading-tight">{user.displayName}</p>
                            <p className="text-[11px] text-muted-foreground leading-tight">{user.email}</p>
                        </div>
                        {user.avatarUrl ? (
                            <img
                                src={user.avatarUrl}
                                alt={user.displayName || ''}
                                className="w-8 h-8 rounded-full ring-2 ring-border"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                                {(user.displayName || user.email)?.[0]?.toUpperCase()}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={logout}
                            className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Cerrar sesión"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                )}
            </div>
        </header>
    );
}
